import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import {
  ThemeProvider,
  neoTheme,
  CompassColor,
  Button,
} from "@noom/wax-component-library";

import { sendBuyflowExit, isInsideWebView } from "../utils/nativeNavigation";
import { useRefillConfirmation } from "../hooks/useRefillConfirmation";
import { MedicationCardHeader } from "../components/prescriptions/MedicationCardHeader";
import { getManufacturerWebsiteLink } from "../modules/refill/manufacturerLink";
import type {
  RefillConfirmationProto,
  RefillDoseOptionProto,
} from "../modules/refill/types";
import { formatPrice } from "../modules/refill/formatPrice";

const PageWrapper = styled.div`
  background-color: ${CompassColor.white};
  min-height: 100vh;
  font-family: "Untitled Sans", sans-serif;
  color: #191717;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 78px;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  padding: 22px 16px 0;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  bottom: 14px;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 30px;
  padding: 0;
  line-height: 1;
  color: #191717;
`;

const HeaderTitle = styled.span`
  font-family: "Untitled Sans", sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #191717;
`;

const Content = styled.div`
  padding: 18px 16px 0;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  box-sizing: border-box;
  flex: 1 0 auto;
`;

const SubheaderText = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: #444444;
  margin: 0 0 30px;
`;

const MedCard = styled(MedicationCardHeader)`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  margin: 0 0 8px;
  color: #191717;
`;

const SectionDescription = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: #444444;
  margin: 0 0 18px;
`;

const DoseOptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionCard = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${(props) => (props.isSelected ? "80px" : "72px")};
  padding: 16px 24px;
  border: 1px solid ${(props) => (props.isSelected ? "#15333D" : "transparent")};
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  background-color: ${(props) => (props.isSelected ? "#DCF5F0" : "#F4F4F4")};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
`;

const RadioCircle = styled.div<{ isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? "#15333D" : "#AAAAAA")};
  background-color: ${(props) =>
    props.isSelected ? "#15333D" : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 16px;
  transition: border-color 0.15s ease;
`;

const RadioDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${CompassColor.white};
`;

const OptionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionLabel = styled.span<{ isSelected: boolean }>`
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: ${(props) => (props.isSelected ? "#05727A" : "#666666")};
  display: block;
  margin-bottom: 4px;
`;

const OptionDose = styled.span`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: #191717;
`;

const PriceColumn = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  padding-left: 12px;
  text-align: right;
`;

const PriceEyebrow = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #666666;
  display: block;
  margin-bottom: 4px;
`;

const OptionPrice = styled.span`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: #191717;
`;

const OtherOptionsTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  color: #191717;
  margin: 6px 0 0;
`;

const DisclaimerText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #666666;
  margin: 20px 0 22px;
`;

const DisclaimerLink = styled.a`
  color: #05727a;
  text-decoration: underline;
`;

const CtaFooter = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  padding: 0 16px 22px;
  box-sizing: border-box;
  background-color: ${CompassColor.white};
`;

const formatDeadline = (deadline?: {
  year?: number;
  month?: number;
  day?: number;
}): string => {
  if (!deadline?.year || !deadline?.month || !deadline?.day) return "";
  const date = new Date(deadline.year, deadline.month - 1, deadline.day);
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
};

export const compareDoseOptions = (
  a: RefillDoseOptionProto,
  b: RefillDoseOptionProto
): number => {
  if (a.isRecommended && b.isRecommended) return 0;
  if (a.isRecommended) return -1;
  if (b.isRecommended) return 1;
  const aCurrent = a.type === "CURRENT";
  const bCurrent = b.type === "CURRENT";
  if (aCurrent && bCurrent) return 0;
  if (aCurrent) return -1;
  if (bCurrent) return 1;
  return 0;
};

export const getInitialSelectedOption = (
  options: RefillDoseOptionProto[] | undefined,
  previouslySelectedInternalDrugId?: string
): RefillDoseOptionProto | null =>
  (previouslySelectedInternalDrugId
    ? options?.find(
        (option) => option.internalDrugId === previouslySelectedInternalDrugId
      )
    : undefined) ??
  options?.find((option) => option.isRecommended) ??
  options?.find((option) => option.type === "CURRENT") ??
  options?.[0] ??
  null;

export const isAtMaxDose = (
  options: RefillDoseOptionProto[] | undefined
): boolean =>
  Boolean(options?.length) &&
  !options?.some((option) => option.type === "NEXT_HIGHEST");

export const getDoseSectionDescription = (atMaxDose: boolean): string =>
  atMaxDose
    ? "You’ve reached the maximum dose. No further increases are available. You can continue with this dose."
    : "If you’re tolerating your medication well, you’re eligible to increase your dose. Higher doses can lead to better weight-loss results.";

export const isMedicareBridgePaymentProgram = (
  paymentProgram: RefillConfirmationProto["paymentProgram"]
): boolean => paymentProgram === "MEDICARE_BRIDGE";

export { formatPrice } from "../modules/refill/formatPrice";

export const ConfirmRefillPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduledFillId = searchParams.get("scheduledFillId");
  const parsedFillId = scheduledFillId ? parseInt(scheduledFillId, 10) : null;
  const fillId =
    parsedFillId !== null && !isNaN(parsedFillId) ? parsedFillId : null;

  const { data, loading, error, confirmSelection } =
    useRefillConfirmation(fillId);
  const [selectedOption, setSelectedOption] =
    useState<RefillDoseOptionProto | null>(null);
  const [confirming, setConfirming] = useState(false);
  const sortedDoseOptions = [...(data?.doseOptions ?? [])].sort(
    compareDoseOptions
  );
  const atMaxDose = isAtMaxDose(sortedDoseOptions);

  useEffect(() => {
    setSelectedOption(
      getInitialSelectedOption(
        data?.doseOptions,
        data?.previouslySelectedInternalDrugId
      )
    );
  }, [data]);

  const handleBack = () => {
    if (isInsideWebView()) {
      sendBuyflowExit();
    } else {
      navigate(-1);
    }
  };

  const handleConfirm = async () => {
    if (!selectedOption || !data) return;

    setConfirming(true);
    try {
      const confirmedLabel = await confirmSelection(
        selectedOption.internalDrugId
      );
      const toast = `Refill confirmed! Your prescription for ${data.medicationDisplayName} (${confirmedLabel || selectedOption.doseLabel}) will be submitted.`;
      if (isInsideWebView()) {
        sendBuyflowExit({ toast });
      } else {
        window.alert(toast);
      }
    } catch {
      if (isInsideWebView()) {
        sendBuyflowExit({ toast: "Failed to confirm refill." });
      } else {
        window.alert("Failed to confirm refill. Please try again.");
      }
    } finally {
      setConfirming(false);
    }
  };

  const getOptionLabel = (option: RefillDoseOptionProto): string | null => {
    if (option.type === "NEXT_HIGHEST") return "Increase dose";
    if (option.type === "CURRENT") {
      return atMaxDose ? "Current dose (Max)" : "Current dose";
    }
    return null;
  };

  if (loading) {
    return (
      <ThemeProvider theme={neoTheme}>
        <PageWrapper>
          <Header>
            <BackButton onClick={handleBack} aria-label="Go back">
              &#8249;
            </BackButton>
            <HeaderTitle>Confirm your refill</HeaderTitle>
          </Header>
          <Content>
            <SubheaderText>Loading...</SubheaderText>
          </Content>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  if (error || !data) {
    return (
      <ThemeProvider theme={neoTheme}>
        <PageWrapper>
          <Header>
            <BackButton onClick={handleBack} aria-label="Go back">
              &#8249;
            </BackButton>
            <HeaderTitle>Confirm your refill</HeaderTitle>
          </Header>
          <Content>
            <SubheaderText>
              {error ||
                "Unable to load refill information. Please try again later."}
            </SubheaderText>
          </Content>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  const selectionDeadline = formatDeadline(data.selectionDeadline);
  const primaryDoseOption = sortedDoseOptions[0];
  const otherDoseOptions = sortedDoseOptions.slice(1);
  const isMedicareBridge = isMedicareBridgePaymentProgram(data.paymentProgram);

  const renderDoseOption = (option: RefillDoseOptionProto) => {
    const isSelected = selectedOption?.internalDrugId === option.internalDrugId;
    const label = getOptionLabel(option);
    const price = formatPrice(option.displayAmount);

    return (
      <OptionCard
        key={option.internalDrugId}
        isSelected={isSelected}
        type="button"
        onClick={() => setSelectedOption(option)}
        role="radio"
        aria-checked={isSelected}
        aria-label={`${label ? `${label}, ` : ""}${option.doseLabel}`}
      >
        <RadioCircle isSelected={isSelected}>
          {isSelected && <RadioDot />}
        </RadioCircle>
        <OptionContent>
          {label && <OptionLabel isSelected={isSelected}>{label}</OptionLabel>}
          <OptionDose>{option.doseLabel}</OptionDose>
        </OptionContent>
        {price && (
          <PriceColumn>
            {isSelected && (
              <PriceEyebrow>
                {isMedicareBridge ? "Copay" : "Estimated pricing*"}
              </PriceEyebrow>
            )}
            <OptionPrice>{price}</OptionPrice>
          </PriceColumn>
        )}
      </OptionCard>
    );
  };

  return (
    <ThemeProvider theme={neoTheme}>
      <PageWrapper>
        <Header>
          <BackButton onClick={handleBack} aria-label="Go back">
            &#8249;
          </BackButton>
          <HeaderTitle>Confirm your refill</HeaderTitle>
        </Header>

        <Content>
          <SubheaderText>
            Request and confirm your next refill.
            {selectionDeadline
              ? ` You can update this until ${selectionDeadline}.`
              : null}
          </SubheaderText>

          <MedCard
            name={data.medicationDisplayName}
            imageUrl={data.medicationImageUrl}
            imageAlt={data.medicationDisplayName}
            imageTransform="rotate(15deg)"
          />

          <SectionTitle>Next refill dose</SectionTitle>
          <SectionDescription>
            {getDoseSectionDescription(atMaxDose)}
          </SectionDescription>

          <DoseOptionsGroup
            role="radiogroup"
            aria-label="Next refill dose options"
          >
            {primaryDoseOption && renderDoseOption(primaryDoseOption)}
            {otherDoseOptions.length > 0 && (
              <>
                <OtherOptionsTitle>Other options</OtherOptionsTitle>
                {otherDoseOptions.map(renderDoseOption)}
              </>
            )}
          </DoseOptionsGroup>
          <DisclaimerText>
            {isMedicareBridge ? (
              "We'll refill your current dose if no action is taken."
            ) : (
              <>
                * Final pricing may vary by manufacturer. Check current pricing at{" "}
                {(() => {
                  const manufacturerLink = getManufacturerWebsiteLink(
                    data.currentInternalDrugId
                  );
                  return manufacturerLink ? (
                    <DisclaimerLink
                      href={manufacturerLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {manufacturerLink.label}
                    </DisclaimerLink>
                  ) : (
                    <span>the manufacturer&apos;s website</span>
                  );
                })()}
                . We'll refill your current dose if no action is taken.
              </>
            )}
          </DisclaimerText>
        </Content>

        <CtaFooter>
          <Button
            colorScheme="primary"
            isFullWidth
            onClick={handleConfirm}
            isDisabled={confirming || !selectedOption}
            style={{
              backgroundColor: selectedOption ? "#05727A" : "#d1d5db",
              borderColor: selectedOption ? "#05727A" : "#d1d5db",
              borderRadius: "29px",
              height: "58px",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {confirming
              ? "Confirming..."
              : selectedOption
                ? `Confirm refill (${selectedOption.doseLabel})`
                : "Confirm refill"}
          </Button>
        </CtaFooter>
      </PageWrapper>
    </ThemeProvider>
  );
};
